/**
 * Pure string<->Date helpers for the shared DatePicker/MonthPicker.
 *
 * Every value here is the same shape a native `<input type="date">` /
 * `<input type="month">` produces: 'YYYY-MM-DD' / 'YYYY-MM'. Dates are
 * constructed and read in LOCAL time — never UTC — because that is what the
 * native inputs did (`new Date('2026-08-25')` would parse as UTC midnight and
 * shift a day in negative-offset timezones; `new Date(2026, 7, 25)` does not).
 */

const YMD_RE = /^(\d{4})-(\d{2})-(\d{2})$/;
const YM_RE = /^(\d{4})-(\d{2})$/;

/** 'YYYY-MM-DD' -> local Date, or null if malformed / not a real calendar date. */
export function parseYmd(value: string | null | undefined): Date | null {
  if (!value) return null;
  const match = YMD_RE.exec(value);
  if (!match) return null;
  const year = Number(match[1]);
  const month = Number(match[2]);
  const day = Number(match[3]);
  const date = new Date(year, month - 1, day);
  // Date auto-corrects overflow (e.g. day 31 in a 30-day month rolls into the
  // next month) instead of throwing — compare back out to reject that.
  if (date.getFullYear() !== year || date.getMonth() !== month - 1 || date.getDate() !== day) {
    return null;
  }
  return date;
}

/** Local Date -> 'YYYY-MM-DD'. */
export function formatYmd(date: Date): string {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
}

/** 'YYYY-MM' -> local Date (day 1), or null if malformed / month out of 1..12. */
export function parseYm(value: string | null | undefined): Date | null {
  if (!value) return null;
  const match = YM_RE.exec(value);
  if (!match) return null;
  const year = Number(match[1]);
  const month = Number(match[2]);
  if (month < 1 || month > 12) return null;
  return new Date(year, month - 1, 1);
}

/** Local Date -> 'YYYY-MM'. */
export function formatYm(date: Date): string {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  return `${y}-${m}`;
}

/** 'YYYY-MM-DD' -> 'dd/MM/yyyy' (Vietnamese date display). '' for invalid input. */
export function formatDateVi(value: string | null | undefined): string {
  const date = parseYmd(value);
  if (!date) return '';
  const d = String(date.getDate()).padStart(2, '0');
  const m = String(date.getMonth() + 1).padStart(2, '0');
  return `${d}/${m}/${date.getFullYear()}`;
}

/** 'YYYY-MM' -> 'Tháng M/YYYY' (no leading zero on the month). '' for invalid input. */
export function formatMonthVi(value: string | null | undefined): string {
  const date = parseYm(value);
  if (!date) return '';
  return `Tháng ${date.getMonth() + 1}/${date.getFullYear()}`;
}

/**
 * Inclusive 'YYYY-MM' range check. Either bound may be omitted for an
 * open-ended range. Relies on zero-padded 'YYYY-MM' sorting lexicographically
 * the same as chronologically.
 */
export function isYmInRange(ym: string, min?: string | null, max?: string | null): boolean {
  if (min && ym < min) return false;
  if (max && ym > max) return false;
  return true;
}

/** Clamp a 'YYYY-MM' value into an inclusive [min, max] range (open-ended bounds allowed). */
export function clampYm(ym: string, min?: string | null, max?: string | null): string {
  if (min && ym < min) return min;
  if (max && ym > max) return max;
  return ym;
}
